/**
 * Kai Micah Mills — power map
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import Konami from 'react-konami-code';
import PowerMapCanvas from './components/PowerMapCanvas';
import MapLegend from './components/MapLegend';
import DetailPanel from './components/DetailPanel';
import { ANCESTORS, LINEAGE_PLACES, ancestorDetail } from './data/ancestors';
import {
  CENTER_NODE,
  HIDDEN_NODE,
  MAIN_NODE_LINKS,
  MOON_NODES,
  POWER_MAP_NODES,
  getDetailForMapClick,
} from './data/portfolioNodes';

const PLACE_COUNTS = ANCESTORS.reduce((counts, person) => {
  const key = person.birthplace || 'unknown';
  counts[key] = (counts[key] || 0) + 1;
  return counts;
}, {});
const PLACE_TOTAL = ANCESTORS.length || 1;

const LINEAGE_LEGEND = LINEAGE_PLACES.map((place) => {
  const count = PLACE_COUNTS[place.id] || 0;
  const pct = Math.round((count / PLACE_TOTAL) * 100);
  return {
    role: place.id,
    label: place.label,
    color: place.color,
    percent: count > 0 && pct === 0 ? '<1' : String(pct),
  };
});

/** June 5, 1998, 10:55 PM — Frederick, MD (US Eastern, EDT = UTC−4 that night). */
const BIRTH_DATE = new Date('1998-06-05T22:55:00-04:00');

function daysAliveFormatted() {
  const msPerDay = 86400000;
  const days = Math.floor((Date.now() - BIRTH_DATE.getTime()) / msPerDay);
  return days.toLocaleString('en-US');
}

const App = () => {
  const [lineageOpen, setLineageOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [placeHighlight, setPlaceHighlight] = useState(null);
  const mapSelectionRef = useRef(null);
  const placeHighlightRef = useRef(null);
  mapSelectionRef.current = selectedId;
  const [largeView, setLargeView] = useState(false);
  const [currentImage, setCurrentImage] = useState('');

  const clearPlaceHighlight = useCallback(() => {
    placeHighlightRef.current = null;
    setPlaceHighlight(null);
  }, []);

  const onSelectNode = useCallback((id) => {
    setSelectedId(id);
  }, []);

  const onToggleLineage = useCallback(() => {
    setLineageOpen((open) => !open);
    setSelectedId(null);
    clearPlaceHighlight();
  }, [clearPlaceHighlight]);

  const onCloseLineage = useCallback(() => {
    setLineageOpen(false);
    setSelectedId(null);
    clearPlaceHighlight();
  }, [clearPlaceHighlight]);

  const onLegendEnter = useCallback((roleId) => {
    placeHighlightRef.current = roleId;
    setPlaceHighlight(roleId);
  }, []);

  const easterEgg = useCallback(() => {
    setSelectedId(HIDDEN_NODE.id);
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (largeView) setLargeView(false);
      else if (selectedId) setSelectedId(null);
      else if (lineageOpen) {
        setLineageOpen(false);
        clearPlaceHighlight();
      } else if (placeHighlight) {
        clearPlaceHighlight();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [largeView, selectedId, lineageOpen, placeHighlight, clearPlaceHighlight]);

  // Clear legend highlight whenever the pointer leaves the legend list
  // (pointerleave alone can miss when the custom cursor / canvas steals the target).
  useEffect(() => {
    if (!placeHighlight) return undefined;
    const onMove = (event) => {
      const list = document.querySelector('.map-legend-list');
      if (!list) {
        clearPlaceHighlight();
        return;
      }
      const under = document.elementFromPoint(event.clientX, event.clientY);
      if (!under || !list.contains(under)) clearPlaceHighlight();
    };
    const onBlur = () => clearPlaceHighlight();
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('blur', onBlur);
    };
  }, [placeHighlight, clearPlaceHighlight]);

  const detailNode = selectedId
    ? ancestorDetail(selectedId) || getDetailForMapClick(selectedId)
    : null;
  const daysAlive = daysAliveFormatted();

  return (
    <>
      {largeView && currentImage?.src ? (
        <div className="large-view">
          <img className="close" src="close.png" alt="Close" onClick={() => setLargeView(false)} />
          <div className="back" onClick={() => setLargeView(false)} />
          <figure className="large-view-frame">
            <img
              className={currentImage.title || currentImage.caption ? 'main has-copy' : 'main'}
              src={currentImage.src}
              alt={currentImage.title || currentImage.caption || ''}
            />
            {currentImage.title || currentImage.caption ? (
              <figcaption className="large-view-copy">
                {currentImage.title ? <p className="large-view-title">{currentImage.title}</p> : null}
                {currentImage.caption && currentImage.caption !== currentImage.title ? (
                  <p className="large-view-caption">{currentImage.caption}</p>
                ) : null}
              </figcaption>
            ) : null}
          </figure>
        </div>
      ) : null}
      <Konami action={easterEgg} />
      <div className="overlay" />
      <main className="cosmos-app">
        <header className="cosmos-header animFade">
          <h1 className="cosmos-wordmark">{CENTER_NODE.title}</h1>
          <p className="cosmos-tagline">{CENTER_NODE.subtitle}</p>
        </header>

        <PowerMapCanvas
          nodes={POWER_MAP_NODES}
          moonNodes={MOON_NODES}
          mainLinks={MAIN_NODE_LINKS}
          selectionRef={mapSelectionRef}
          placeHighlightRef={placeHighlightRef}
          onSelectNode={onSelectNode}
          onToggleLineage={onToggleLineage}
          onCloseLineage={onCloseLineage}
          lineageOpen={lineageOpen}
          centerConfig={CENTER_NODE}
        />

        {lineageOpen ? (
          <MapLegend
            className="map-legend-interactive map-legend-places"
            title="Birthplace"
            entries={LINEAGE_LEGEND}
            label="Birthplace legend"
            interactive
            activeRole={placeHighlight}
            onEntryEnter={onLegendEnter}
            onEntryLeave={clearPlaceHighlight}
          />
        ) : (
          <MapLegend
            className="map-legend-interactive"
            interactive
            activeRole={placeHighlight}
            onEntryEnter={onLegendEnter}
            onEntryLeave={clearPlaceHighlight}
          />
        )}

        <DetailPanel
          node={detailNode}
          onClose={() => setSelectedId(null)}
          onImageOpen={(image) => {
            const next = typeof image === 'string' ? { src: image } : image;
            if (!next?.src) return;
            setCurrentImage(next);
            setLargeView(true);
          }}
        />

        <footer className="cosmos-footer">
          <div className="cosmos-age animFade three" id="age">
            {daysAlive} days alive
          </div>
          <div className="cosmos-social animFade four">
            <a href="https://x.com/kaimicahmills" target="_blank" rel="noreferrer">
              <img src="/x-twitter.svg" alt="X" />
            </a>
            <a href="https://linkedin.com/in/kaimicahmills" target="_blank" rel="noreferrer">
              <i className="fa fa-linkedin" />
            </a>
            <a href="https://youtube.com/@kaimicahmills" target="_blank" rel="noreferrer">
              <i className="fa fa-youtube" />
            </a>
            <a href="https://github.com/kaimicahmills" target="_blank" rel="noreferrer">
              <i className="fa fa-github" />
            </a>
            <a href="https://instagram.com/cryokai" target="_blank" rel="noreferrer">
              <i className="fa fa-instagram" />
            </a>
            <a href="mailto:kaimicahmills@gmail.com">
              <i className="fa fa-envelope" />
            </a>
          </div>
          <div className="cosmos-copy animFade five"><span className="cosmos-copy-mark">©</span> {new Date().getFullYear()}</div>
        </footer>
      </main>
    </>
  );
};

export default App;
